import { useProfileMutation } from "./useProfileMutation";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import type { UpdateProfileInput } from "@yalana/contracts";
import { authClient } from "../../../shared/infrastructure/auth-client";
import { useAuth } from "../context";

/**
 * Hook for updating user profile (name + email).
 * Returns form state and submit handler.
 */
export function useProfileForm() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [values, setValues] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    preferredLanguage: (user?.preferredLanguage ?? "en") as "en" | "es",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useProfileMutation();

  const handleChange = (
    field: "name" | "email" | "preferredLanguage",
    value: string,
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (values.name && values.name.trim().length === 0) {
      newErrors.name = t("auth.nameRequired");
    }

    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      newErrors.email = t("auth.invalidEmail");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    const input: UpdateProfileInput = {};
    if (values.name && values.name !== user?.name) {
      input.name = values.name;
    }
    if (values.email && values.email !== user?.email) {
      input.email = values.email;
    }
    if (values.preferredLanguage !== user?.preferredLanguage) {
      input.preferredLanguage = values.preferredLanguage;
    }

    if (Object.keys(input).length === 0) {
      notifications.show({
        title: t("auth.info"),
        message: t("auth.noChanges"),
        color: "blue",
        autoClose: 3000,
      });
      return;
    }

    mutation.mutate(input);
  };

  return {
    values,
    errors,
    handleChange,
    submit,
    isPending: mutation.isPending,
  };
}

/**
 * Hook for changing password.
 * Returns form state and submit handler.
 */
export function usePasswordForm() {
  const { t } = useTranslation();
  const [values, setValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPending, setIsPending] = useState(false);

  const handleChange = (
    field: "currentPassword" | "newPassword" | "confirmPassword",
    value: string,
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!values.currentPassword.trim()) {
      newErrors.currentPassword = t("auth.currentPasswordRequired");
    }

    if (values.newPassword.length < 8) {
      newErrors.newPassword = t("auth.passwordMinLength");
    }

    if (!/[A-Z]/.test(values.newPassword)) {
      newErrors.newPassword = t("auth.passwordNeedsUppercase");
    }

    if (!/[0-9]/.test(values.newPassword)) {
      newErrors.newPassword = t("auth.passwordNeedsNumber");
    }

    if (values.newPassword !== values.confirmPassword) {
      newErrors.confirmPassword = t("auth.passwordsDoNotMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    setIsPending(true);
    try {
      await authClient.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      notifications.show({
        title: t("auth.success"),
        message: t("auth.passwordChanged"),
        color: "green",
        autoClose: 3000,
      });

      setValues({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("auth.passwordChangeFailed");
      notifications.show({
        title: t("auth.error"),
        message,
        color: "red",
        autoClose: 3000,
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    values,
    errors,
    handleChange,
    submit,
    isPending,
  };
}
